import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { mapContextMenuAtom, showCommentPanelAtom, showSatelliteImagePanelAtom } from '../stores/mapStore';
import {
  currentFinderTypeAtom,
  satelliteSearchPointAtom,
  SatelliteTypeEnum,
} from '../panels/ForestWatchPanel/SatelliteImageTab/store';
import { useAddCommentAtCoordinates } from './hooks/useAddCommentAtCoordinates';

interface MapContextMenuProps {}

export interface ContextMenuPosition {
  x: number;
  y: number;
  lng: number;
  lat: number;
}

const MapContextMenu: React.FC<MapContextMenuProps> = () => {
  const { t } = useTranslation();
  const [contextMenu, setContextMenu] = useAtom(mapContextMenuAtom);
  const [showCommentPanel, setShowCommentPanel] = useAtom(showCommentPanelAtom);
  const [, setShowSatelliteImagePanel] = useAtom(showSatelliteImagePanelAtom);
  const [currentFinderType, setCurrentFinderType] = useAtom(currentFinderTypeAtom);
  const [, setSatelliteSearchPoint] = useAtom(satelliteSearchPointAtom);
  const [showOpenInSubmenu, setShowOpenInSubmenu] = useState(false);
  const [showSatelliteSubmenu, setShowSatelliteSubmenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { addCommentAtCoordinates } = useAddCommentAtCoordinates();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
        setShowOpenInSubmenu(false);
        setShowSatelliteSubmenu(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setContextMenu(null);
        setShowOpenInSubmenu(false);
        setShowSatelliteSubmenu(false);
      }
    };

    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu, setContextMenu]);

  const handleCopyCoordinates = () => {
    if (contextMenu) {
      const coordinates = `${contextMenu.lng.toFixed(6)}, ${contextMenu.lat.toFixed(6)}`;
      navigator.clipboard.writeText(coordinates);
      setContextMenu(null);
    }
  };

  const handleAddComment = async () => {
    if (contextMenu) {
      await addCommentAtCoordinates(contextMenu.lng, contextMenu.lat);
      setShowCommentPanel(true);
      setContextMenu(null);
    }
  };

  const handlePasteHere = async () => {
    if (contextMenu) {
      try {
        const text = await navigator.clipboard.readText();

        // Try to parse as coordinates (lng,lat or lat,lng formats)
        const coordinatePattern = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/;
        const match = text.trim().match(coordinatePattern);

        if (match) {
          const num1 = parseFloat(match[1]);
          const num2 = parseFloat(match[2]);

          // Check if it's coordinates (lng should be -180 to 180, lat should be -90 to 90)
          let lng, lat;
          if (Math.abs(num1) <= 180 && Math.abs(num2) <= 90) {
            // Assume first is lng, second is lat
            lng = num1;
            lat = num2;
          } else if (Math.abs(num2) <= 180 && Math.abs(num1) <= 90) {
            // Assume first is lat, second is lng
            lng = num2;
            lat = num1;
          }

          if (lng !== undefined && lat !== undefined) {
            // Found valid coordinates, create comment with them
            await addCommentAtCoordinates(lng, lat);
            setShowCommentPanel(true);
          } else {
            // Create comment with pasted text as description at current coordinates
            await addCommentAtCoordinates(contextMenu.lng, contextMenu.lat, {
              description: text ? `<p>${text}</p>` : undefined,
            });
            setShowCommentPanel(true);
          }
        } else {
          // Create comment with pasted text as description at current coordinates
          await addCommentAtCoordinates(contextMenu.lng, contextMenu.lat, {
            description: text ? `<p>${text}</p>` : undefined,
          });
          setShowCommentPanel(true);
        }

        setContextMenu(null);
      } catch (err) {
        console.error('Failed to read clipboard contents: ', err);
      }
    }
  };

  const handleOpenIn = (service: string) => {
    if (contextMenu) {
      const { lng, lat } = contextMenu;
      let url = '';

      switch (service) {
        case 'google':
          url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
          break;
        case 'apple':
          url = `http://maps.apple.com/?q=${lat},${lng}`;
          break;
        case 'openstreetmap':
          url = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`;
          break;
        default:
          return;
      }

      window.open(url, '_blank');
      setContextMenu(null);
      setShowOpenInSubmenu(false);
    }
  };

  const handleSearchSatelliteHere = (type?: SatelliteTypeEnum) => {
    if (contextMenu) {
      setSatelliteSearchPoint({
        lng: contextMenu.lng,
        lat: contextMenu.lat,
        timestamp: Date.now(),
      });
      const targetType =
        type ||
        (currentFinderType === SatelliteTypeEnum.S2_SINGLE
          ? SatelliteTypeEnum.S2_SINGLE
          : SatelliteTypeEnum.LANDSAT);
      setCurrentFinderType(targetType);
      setShowSatelliteImagePanel(true);
      setContextMenu(null);
      setShowSatelliteSubmenu(false);
      setShowOpenInSubmenu(false);
    }
  };

  if (!contextMenu) return null;

  return (
    <MenuContainer
      ref={menuRef}
      style={{
        left: contextMenu.x,
        top: contextMenu.y,
      }}
    >
      <MenuItem onClick={handleCopyCoordinates}>
        <CoordinateText>
          {contextMenu.lng.toFixed(6)}, {contextMenu.lat.toFixed(6)}
        </CoordinateText>
      </MenuItem>

      <MenuSeparator />

      <MenuItem onClick={handleAddComment}>
        {t('context_menu_add_comment')}
      </MenuItem>

      <MenuItem onClick={handlePasteHere}>
        <MenuItemContent>
          <span>{t('context_menu_paste_here')}</span>
          <KeyboardShortcut>⌘V</KeyboardShortcut>
        </MenuItemContent>
      </MenuItem>

      <MenuItem
        onMouseEnter={() => {
          setShowSatelliteSubmenu(true);
          setShowOpenInSubmenu(false);
        }}
        onMouseLeave={() => setShowSatelliteSubmenu(false)}
        style={{ position: 'relative' }}
        onClick={() => handleSearchSatelliteHere()}
      >
        <MenuItemContent>
          <span>{t('context_menu_search_satellite_here')}</span>
          <ArrowIcon>›</ArrowIcon>
        </MenuItemContent>

        {showSatelliteSubmenu && (
          <Submenu>
            <SubMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleSearchSatelliteHere(SatelliteTypeEnum.LANDSAT);
              }}
            >
              {t('context_menu_search_satellite_landsat', 'Landsat')}
            </SubMenuItem>
            <SubMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleSearchSatelliteHere(SatelliteTypeEnum.S2_SINGLE);
              }}
            >
              {t('context_menu_search_satellite_sentinel2', 'Sentinel-2')}
            </SubMenuItem>
            <SubMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleSearchSatelliteHere(SatelliteTypeEnum.S2_MOSAIC);
              }}
            >
              {t('context_menu_search_satellite_s2_mosaic', 'Sentinel-2 Mosaic')}
            </SubMenuItem>
          </Submenu>
        )}
      </MenuItem>

      <MenuItem
        onMouseEnter={() => {
          setShowOpenInSubmenu(true);
          setShowSatelliteSubmenu(false);
        }}
        onMouseLeave={() => setShowOpenInSubmenu(false)}
        style={{ position: 'relative' }}
      >
        <MenuItemContent>
          <span>{t('context_menu_open_in')}</span>
          <ArrowIcon>›</ArrowIcon>
        </MenuItemContent>

        {showOpenInSubmenu && (
          <Submenu>
            <SubMenuItem onClick={() => handleOpenIn('google')}>
              {t('context_menu_open_in_google')}
            </SubMenuItem>
            <SubMenuItem onClick={() => handleOpenIn('apple')}>
              {t('context_menu_open_in_apple')}
            </SubMenuItem>
            <SubMenuItem onClick={() => handleOpenIn('openstreetmap')}>
              {t('context_menu_open_in_osm')}
            </SubMenuItem>
          </Submenu>
        )}
      </MenuItem>
    </MenuContainer>
  );
};

export default MapContextMenu;

const MenuContainer = styled.div`
  position: fixed;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 180px;
  z-index: 10000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;

  @media (max-width: 768px) {
    min-width: 200px;
    font-size: 14px;
    border-radius: 8px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 480px) {
    min-width: 220px;
    font-size: 15px;
    padding: 6px 0;
  }
`;

const MenuItem = styled.div`
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background-color: #f5f5f5;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    padding: 12px 18px;
    font-size: 15px;
    min-height: 44px;

    &:hover {
      background-color: #e8f4fd;
    }
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CoordinateText = styled.span`
  font-family: Monaco, 'Lucida Console', monospace;
  font-size: 12px;
  color: #666;
`;

const KeyboardShortcut = styled.span`
  color: #999;
  font-size: 11px;
`;

const ArrowIcon = styled.span`
  color: #666;
  font-weight: bold;
`;

const MenuSeparator = styled.div`
  height: 1px;
  background-color: #e0e0e0;
  margin: 4px 0;
`;

const Submenu = styled.div`
  position: absolute;
  left: 100%;
  top: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 140px;
  margin-left: -1px;
`;

const SubMenuItem = styled.div`
  padding: 6px 12px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;